import _ from 'lodash'
import { name } from './symbols'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { ViewerAPI } from 'thunderbolt-viewer-manager-types'
import {
	BrowserWindow,
	BrowserWindowSymbol,
	EditorFeatureConfigSymbol,
	ExperimentsSymbol,
	IStructureAPI,
	StructureAPI,
	ViewerManagerStructureAPISymbol,
} from '@wix/thunderbolt-symbols'
import {
	ContainerMeasures,
	CSSOverridesInSelector,
	DetachedComp,
	DetachedComps,
	IResponsiveDetachApi,
	LayoutOverrides,
	MeasureBoundingRect,
	MeasureBoundingRectWithOffsets,
	TransformationOverrides,
	ViewerManagerResponsiveApiEditorConfig,
} from './types'
import {
	getDisplayedId,
	getFullId,
	getItemId,
	getRotateString,
	getScaleString,
	getSkewString,
	getTranslateString,
	isDisplayedOnly,
	isRepeater,
	isStyleItemContainsTransformProps,
	orderedTransformationKeys,
	getRuntimeStyleOverridesManager,
} from '@wix/thunderbolt-commons'

import type { CssOverrides } from '@wix/thunderbolt-commons'
import { ComponentBoundingBoxApiSymbol, ICompBoundingBoxApi } from 'ds-feature-viewer-manager-layout-api'

export const ResponsiveDetachAPI = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		StructureAPI,
		ViewerManagerStructureAPISymbol,
		ComponentBoundingBoxApiSymbol,
		ExperimentsSymbol,
	],
	(
		config: ViewerManagerResponsiveApiEditorConfig,
		windowObject: NonNullable<BrowserWindow>,
		structureAPI: IStructureAPI,
		viewerManagerStructureAPI: ViewerAPI['structure'],
		componentBoundingBoxApi: ICompBoundingBoxApi
	): IResponsiveDetachApi => {
		const runtimeStyleOverridesManager = getRuntimeStyleOverridesManager()
		let detachedComps: DetachedComps = {}

		// ignore components starting with "Container" e.g Containerc1dmp is the content wrapper of c1dmp and will be handled as part of the c1dmp component
		// ignore PAGE_SECTIONS - should not lock this container because its just semantic wrapper with display contents
		// ignore pinned-layer - should not lock this container
		const filterAddedStructureComponents = (ancestors: Array<string>) =>
			ancestors.filter((containerId) => {
				const isDisplayedContainer =
					!containerId.startsWith('Container') &&
					!containerId.startsWith('PAGE_SECTIONS') &&
					!containerId.endsWith('-pinned-layer')

				// ignore pinned - should not lock this container because its just semantic wrapper with display contents
				// ignore relative - should not lock this container because it's a wrapper
				return isDisplayedContainer && !containerId.includes('_pinned') && !containerId.includes('_relative')
			})

		const getAncestorsDisplayedIds = (compId: string) => {
			const isRepeatedComponent = isDisplayedOnly(compId)
			if (!isRepeatedComponent) {
				return config.compsData[compId].ancestors
			}
			const nonInflatedAncestors = config.compsData[getFullId(compId)].ancestors
			const itemId = getItemId(compId)
			let shouldInflateId = true
			// Need to inflate only components that are inside the repeater
			return nonInflatedAncestors.map((ancestor) => {
				if (isRepeater(structureAPI.get(ancestor).componentType)) {
					shouldInflateId = false
				}
				if (shouldInflateId) {
					return getDisplayedId(ancestor, itemId)
				}
				return ancestor
			})
		}

		const getAncestors = (compId: string) => filterAddedStructureComponents(getAncestorsDisplayedIds(compId))

		const getLayoutContainer = (containerId: string) => {
			const nonInflatedContainerId = getFullId(containerId) // support repeater case
			return windowObject.document.querySelector(`.${nonInflatedContainerId}-container`) // layoutToCss generates css based on template id
		}

		const getContainerDisplay = (compId: string): string => {
			return windowObject.getComputedStyle(getLayoutContainer(compId)!).display
		}

		const addPxToMeasure = (measure: number | string): string => {
			return isNaN(measure as number) ? (measure as string) : `${measure}px`
		}

		const isGridContainer = (containerId: string) => getContainerDisplay(containerId) === 'grid'
		const shouldLockChildren = (containerId: string) => !isGridContainer(containerId)

		const getChildren = (containerId: string) =>
			viewerManagerStructureAPI.getStructure(containerId).components || []

		const measureBoundingRect = (compId: string): MeasureBoundingRect => {
			const compBoundingClientRect = windowObject.document.getElementById(compId)!.getBoundingClientRect()

			return {
				left: compBoundingClientRect.left,
				top: compBoundingClientRect.top,
				width: compBoundingClientRect.width,
				height: compBoundingClientRect.height,
			}
		}

		const getOverflowWrapper = (containerId: string) => {
			const nonInflatedContainerId = getFullId(containerId) // support repeater case
			return windowObject.document.querySelector(`.${nonInflatedContainerId}-overflow-wrapper`) // layoutToCss generates css based on template id
		}

		const getContainerScroll = (containerId: string) => {
			const overflowWrapper = getOverflowWrapper(containerId)
			const { scrollTop: top = 0, scrollLeft: left = 0 } = overflowWrapper || {}
			return {
				top,
				left,
			}
		}

		const getCompsBoundingRect = (compIds: Array<string>) => {
			const compsBoundingRects: { [compId: string]: MeasureBoundingRectWithOffsets } = {}

			const getCompsBoundingRectRecursive = (currCompId: string): MeasureBoundingRectWithOffsets => {
				if (compsBoundingRects[currCompId]) {
					return compsBoundingRects[currCompId]
				}

				const parentId = getAncestors(currCompId)[0]
				const selfBoundingRect = measureBoundingRect(currCompId)

				let currBoundingRect: MeasureBoundingRectWithOffsets
				if (parentId) {
					const parentBoundingRect = getCompsBoundingRectRecursive(parentId)
					const containerScroll = getContainerScroll(parentId)
					currBoundingRect = {
						offsetTop: containerScroll.top + selfBoundingRect.top - parentBoundingRect.top,
						offsetLeft: containerScroll.left + selfBoundingRect.left - parentBoundingRect.left,
						...selfBoundingRect,
					}
				} else {
					currBoundingRect = {
						offsetTop: 0,
						offsetLeft: 0,
						...selfBoundingRect,
					}
				}

				compsBoundingRects[currCompId] = currBoundingRect
				return currBoundingRect
			}

			compIds.forEach(getCompsBoundingRectRecursive)

			return compsBoundingRects
		}

		const measureContainer = (compId: string): ContainerMeasures => {
			const containerNode = windowObject.document.getElementById(compId)!
			const overflowWrapper = getOverflowWrapper(compId)
			const display = getContainerDisplay(compId)
			const contentWrapper = getLayoutContainer(compId)!
			const contentWrapperComputedStyle = windowObject.getComputedStyle(contentWrapper)

			return {
				containerMeasures: {
					display,
					hasInlineContent: !containerNode.className.includes(`${getFullId(compId)}-container`),
				},
				...(overflowWrapper && {
					overflowWrapper: {
						scrollWidth: overflowWrapper.scrollWidth,
						scrollHeight: overflowWrapper.scrollHeight,
					},
				}),
				...(display === 'grid' && {
					gridMeasures: {
						gridTemplateRows: contentWrapperComputedStyle.gridTemplateRows,
						gridTemplateColumns: contentWrapperComputedStyle.gridTemplateColumns,
					},
				}),
			}
		}

		const getPlaceholderOverrides = (
			childrenToLock: Array<string>,
			ancestorsToLock: Array<string>,
			compsBoundingRects: { [compId: string]: MeasureBoundingRectWithOffsets },
			ancestorsMeasuresForPlaceholders: { [compId: string]: ContainerMeasures }
		): CSSOverridesInSelector => {
			const placeholderOverrides: CSSOverridesInSelector = []

			childrenToLock.forEach((compId) => {
				const compBoundingRect = compsBoundingRects[compId]

				placeholderOverrides.push({
					selector: `#${compId}`,
					css: {
						position: 'absolute',
						left: `${compBoundingRect.offsetLeft}px`,
						top: `${compBoundingRect.offsetTop}px`,
						width: `${compBoundingRect.width}px`,
						height: `${compBoundingRect.height}px`,
						margin: '0',
					},
				})
			})

			ancestorsToLock.forEach((containerId) => {
				const containerBoundingRect = compsBoundingRects[containerId]
				const measures = ancestorsMeasuresForPlaceholders[containerId]

				const rootCss = {
					width: `${containerBoundingRect.width}px`,
					height: `${containerBoundingRect.height}px`,
				}

				const contentWrapperCss = {
					...(measures.gridMeasures && {
						'grid-template-rows': measures.gridMeasures.gridTemplateRows,
						'grid-template-columns': measures.gridMeasures.gridTemplateColumns,
					}),
					...(measures.overflowWrapper && {
						width: `${measures.overflowWrapper.scrollWidth}px`,
						height: `${measures.overflowWrapper.scrollHeight}px`,
					}),
				}

				placeholderOverrides.push({
					selector: `#${containerId}`,
					css: rootCss,
				})

				placeholderOverrides.push({
					selector: measures.containerMeasures.hasInlineContent
						? `#${containerId} .${getFullId(containerId)}-container`
						: `#${containerId}`,
					css: contentWrapperCss,
				})
			})

			return placeholderOverrides
		}

		const defaultDetachedCompStyles = {
			position: 'fixed',
			'grid-area': 'auto',
			margin: 'auto',
			bottom: 'auto',
			right: 'auto',
			'min-height': 'auto',
			'min-width': 'auto',
		}

		const layoutOverridesKeys = {
			top: 'top',
			left: 'left',
			width: 'width',
			height: 'height',
			minWidth: 'min-width',
			minHeight: 'min-height',
		}

		const supportedLayoutOverridesProps: Array<'top' | 'left' | 'width' | 'height' | 'minWidth' | 'minHeight'> = [
			'top',
			'left',
			'width',
			'height',
			'minWidth',
			'minHeight',
		]

		const getRotationStyles = (rotationOverrides?: number): CssOverrides =>
			typeof rotationOverrides === 'undefined' ? {} : { transform: `rotate(${rotationOverrides}deg)` }

		const getTransformationStyles = (transformationData?: TransformationOverrides): CssOverrides => {
			if (transformationData && isStyleItemContainsTransformProps(transformationData)) {
				const { scale, rotate, translate, skew } = transformationData

				const transformationsString = {
					translate: getTranslateString(translate),
					scale: getScaleString(scale),
					rotate: getRotateString(rotate),
					skew: getSkewString(skew),
				}

				const transformStringValue = orderedTransformationKeys
					.reduce(
						(transformString, transformKey) =>
							`${transformString}${
								transformationsString[transformKey as 'translate' | 'scale' | 'rotate' | 'skew']
							}`,
						''
					)
					.trim()
				if (transformStringValue) {
					return {
						transform: transformStringValue,
					}
				}
			}
			return {}
		}

		const getDetachedStyles = (
			compId: string,
			boundingBox: MeasureBoundingRect,
			layoutOverrides?: LayoutOverrides,
			rotationOverrides?: number,
			transformationOverrides?: TransformationOverrides
		): CssOverrides => {
			const detachedCompStyles = Object.keys(boundingBox).reduce<CssOverrides>(
				(res: CssOverrides, propName: string) => {
					res[propName] = addPxToMeasure(boundingBox[propName as 'top' | 'left' | 'width' | 'height'])
					return res
				},
				{}
			)

			const layoutOverridesCss = layoutOverrides
				? supportedLayoutOverridesProps.reduce<CssOverrides>((res, key) => {
						if (!_.isUndefined(layoutOverridesKeys[key])) {
							res[layoutOverridesKeys[key]] = addPxToMeasure(layoutOverrides[key])
						}

						return res
				  }, {})
				: {}
			const rotateStyles = getRotationStyles(rotationOverrides)
			const transformationStyles = getTransformationStyles(transformationOverrides)

			return {
				...defaultDetachedCompStyles,
				...detachedCompStyles,
				...layoutOverridesCss,
				...rotateStyles,
				...transformationStyles,
			}
		}

		const getDetachedItemsOverrides = (compsDetachData: DetachedComps): CSSOverridesInSelector => {
			return Object.keys(compsDetachData).map<{
				selector: string
				css: CssOverrides
			}>((compId) => {
				return {
					selector: `#${compId}`,
					css: getDetachedStyles(
						compId,
						compsDetachData[compId].boundingBox,
						compsDetachData[compId].layoutOverrides,
						compsDetachData[compId].rotationOverrides,
						compsDetachData[compId].transformationOverrides
					),
				}
			})
		}

		const updateDetachedCompsAndSetCss = (detachedCompsToUpdate: DetachedComps) => {
			detachedComps = {
				...detachedComps,
				...detachedCompsToUpdate,
			}

			getDetachedItemsOverrides(detachedComps).forEach((override) =>
				runtimeStyleOverridesManager.setItemCssOverrides(override.css, override.selector, windowObject)
			)
		}

		const shouldLockAncestor = (compDetachedData: DetachedComp) => compDetachedData.position !== 'absolute'

		const startDetach = (compsDetachData: DetachedComps) => {
			const compIds = Object.keys(compsDetachData).filter((compId) => shouldLockAncestor(compsDetachData[compId]))
			const ancestorsToLock = [...new Set(compIds.flatMap((compId) => getAncestors(compId)))]

			const shouldLockComp = (compId: string) => !compsDetachData[compId]

			const childrenToLock = ancestorsToLock.flatMap((containerId) =>
				shouldLockChildren(containerId) ? getChildren(containerId).filter(shouldLockComp) : []
			)

			const compsToLock = childrenToLock.concat(ancestorsToLock)

			const compsBoundingRects = getCompsBoundingRect(compsToLock)

			const ancestorsMeasuresForPlaceholders = ancestorsToLock.reduce<{ [compId: string]: ContainerMeasures }>(
				(result, containerId) => {
					result[containerId] = measureContainer(containerId)

					return result
				},
				{}
			)

			const placeholderOverrides = getPlaceholderOverrides(
				childrenToLock,
				ancestorsToLock,
				compsBoundingRects,
				ancestorsMeasuresForPlaceholders
			)

			placeholderOverrides.forEach((override) =>
				runtimeStyleOverridesManager.setItemCssOverrides(override.css, override.selector, windowObject)
			)

			updateDetachedCompsAndSetCss(compsDetachData)
		}

		const detachMulti = (compIds: Array<string>) => {
			if (Object.keys(detachedComps).length > 0) {
				return
			}

			const compsDetachData = compIds.reduce<DetachedComps>((result, compId) => {
				const node = windowObject.document.getElementById(compId)
				if (node) {
					const nonRotatedClientBoundingBox = componentBoundingBoxApi.getRelativeToViewportBoundingBox(compId)
					const boundingBox = {
						left: nonRotatedClientBoundingBox.absoluteLeft,
						top: nonRotatedClientBoundingBox.absoluteTop,
						width: nonRotatedClientBoundingBox.width,
						height: nonRotatedClientBoundingBox.height,
					}

					result[compId] = {
						position: windowObject.getComputedStyle(node).position,
						boundingBox,
					}
				}

				return result
			}, {})

			startDetach(compsDetachData)
		}

		const updateDetached = (compId: string, layoutOverrides: LayoutOverrides) => {
			const detachedComp = detachedComps[compId]
			if (!detachedComp) {
				return
			}

			updateDetachedCompsAndSetCss({
				[compId]: {
					...detachedComp,
					layoutOverrides: {
						...layoutOverrides,
					},
				},
			})
		}

		const updateDetachedRotation = (compId: string, rotationOverrides: number) => {
			const detachedComp = detachedComps[compId]
			if (!detachedComp) {
				throw new Error('Component must be detached before updating rotation')
			}

			updateDetachedCompsAndSetCss({
				[compId]: {
					...detachedComp,
					rotationOverrides,
				},
			})
		}

		const updateDetachedTransformation = (compId: string, transformationOverrides: TransformationOverrides) => {
			const detachedComp = detachedComps[compId]
			if (!detachedComp) {
				throw new Error('Component must be detached before updating transformation')
			}

			updateDetachedCompsAndSetCss({
				[compId]: {
					...detachedComp,
					transformationOverrides: {
						...transformationOverrides,
					},
				},
			})
		}

		const clearDetached = () => {
			runtimeStyleOverridesManager.clearAllItemsCssOverrides(windowObject)
			detachedComps = {}
		}

		const detach = (compId: string) => detachMulti([compId])

		type DetachContainerOptions = {
			containerOverrides?: CssOverrides
			childrenOverrides?: { [compId: string]: CssOverrides }
		}

		const detachContainer = (compId: string, options: DetachContainerOptions = {}) => {
			type OverridePropertyTarget = 'Container' | 'GridContainer'
			const supportedContainerOverrides: Record<string, OverridePropertyTarget> = {
				paddingTop: 'GridContainer',
				paddingBottom: 'GridContainer',
				paddingRight: 'GridContainer',
				paddingLeft: 'GridContainer',
			}
			const supportedChildrenOverrides = {
				width: true,
				height: true,
				marginTop: true,
				marginBottom: true,
				marginRight: true,
				marginLeft: true,
			}

			detach(compId)
			if (options.containerOverrides) {
				const containerOverrides: Record<string, any> = {}
				const gridContainerOverrides: Record<string, any> = {}

				_.forIn(options.containerOverrides, (value, key) => {
					if (!(key in supportedContainerOverrides)) {
						return
					}

					const propertyTarget = supportedContainerOverrides[key]
					const kebabKey = _.kebabCase(key)

					if (propertyTarget === 'Container') {
						containerOverrides[kebabKey] = value
					} else if (propertyTarget === 'GridContainer') {
						gridContainerOverrides[kebabKey] = value
					}
				})
				runtimeStyleOverridesManager.setItemCssOverrides(containerOverrides, `#${compId}`, windowObject)
				runtimeStyleOverridesManager.setItemCssOverrides(
					gridContainerOverrides,
					`.${getFullId(compId)}-container`,
					windowObject
				)
			}
			if (options.childrenOverrides) {
				Object.entries(options.childrenOverrides).forEach(([childId, childOverrides]) => {
					const cssOverrides = _.reduce(
						childOverrides,
						(agg: Record<string, string>, value: any, key: string) => {
							if (key in supportedChildrenOverrides) {
								agg[_.kebabCase(key)] = value
							}
							return agg
						},
						{}
					)
					runtimeStyleOverridesManager.setItemCssOverrides(cssOverrides, `#${childId}`, windowObject)
				})
			}
		}

		return {
			detach,
			detachMulti,
			// @ts-expect-error - LayoutOverrides types should be fixed in DM interface
			updateDetached,
			updateDetachedTransformation,
			updateDetachedRotation,
			detachContainer,
			clearDetached,
		}
	}
)
